import {
  fetchJson,
  post,
  postJson,
  userAction,
} from '@/util/helpers/ajax-helpers';
import ViewRef from '../util/ViewRef';
import { loading, completed, standardErrorHandler } from './helpers';
import {
  MODAL_INTERACTION,
  MODAL_DATA_LOADED,
  MODAL_SAVE_CLICKED,
  VIEW_SAVED,
  MODAL_HIDDEN,
  TABLE_COLUMNS_LOADED,
} from './constants';
import ModalSaveLayers from '../components/modals/ModalSaveLayers';
import ModalCollaborators from '../components/modals/ModalCollaborators';
import ModalHiddenColumns from '../components/modals/ModalHiddenColumns';
import ModalSqlEditor from '../components/modals/ModalSqlEditor';
import ModalAggregate from '../components/modals/ModalAggregate';
import ModalJoin from '../components/modals/ModalJoin';
import ModalWhere from '../components/modals/ModalWhere';
import ModalSelect from '../components/modals/ModalSelect';
import ModalAddColumn from '../components/modals/ModalAddColumn';
import ModalRearrangeColumns from '../components/modals/ModalRearrangeColumns';
import ModalRenameColumns from '../components/modals/ModalRenameColumns';
import ModalAddAlert from '../components/modals/ModalAddAlert';
import ModalAddDashboard from '../components/modals/ModalAddDashboard';
import ModalOrderBy from '../components/modals/ModalOrderBy';
import ModalCsvUpload from '../components/modals/ModalCsvUpload';
import HiddenColumn from '../util/HiddenColumn';
import Onboarding from '../util/Onboarding';
import { checkViewStatus } from './actions-views';
import { loadTables, loadViewsData, testSql } from './actions-misc';
import { saveAlert } from './actions-alerts';
import { addDashboard } from './actions-dashboards';
import {
  ACTION_FILTER,
  ACTION_SAVE_QUERY,
} from '../components/HighlightFeature';
import BadRequestException from '../util/BadRequestException';

export function modalInteraction(modalType, props) {
  return completed(MODAL_INTERACTION, { modalType, props });
}

export function openModal(modal, props = {}) {
  return (dispatch, getState) => {
    const isFreePlan = getState().datasource.maxSteps < 999;
    const isHeroku = getState().datasource.isHerokuBeta;
    dispatch(loading(MODAL_DATA_LOADED));
    dispatch(
      completed(MODAL_DATA_LOADED, {
        modal,
        props,
        isFreePlan,
        isHeroku,
      }),
    );
  };
}

export function modalSaveClicked(
  currentModal,
  state,
  existingLayer,
  templateToReplace,
) {
  if (currentModal === ModalSaveLayers) {
    userAction(ACTION_SAVE_QUERY);
    return saveView(state);
  }
  if (currentModal === ModalCollaborators) {
    userAction('use-invite');
    return updateCollaborators(state);
  }
  if (currentModal === ModalHiddenColumns) {
    userAction('use-hide-tables');
    return updateHiddenColumns(state);
  }
  if (currentModal === ModalSqlEditor) {
    userAction('use-sql');
    return testSql(state, existingLayer);
  }
  if (currentModal === ModalAddDashboard) {
    return addDashboard(state);
  }
  if (currentModal === ModalAddAlert) {
    userAction('use-add-alert');
    return saveAlert(state);
  }
  if (currentModal === ModalAggregate) {
    Onboarding.headerUnderstood();
    userAction('use-calculate');
  } else if (currentModal === ModalJoin) {
    userAction('use-join');
  } else if (currentModal === ModalWhere) {
    Onboarding.headerUnderstood();
    userAction(ACTION_FILTER);
  } else if (currentModal === ModalOrderBy) {
    Onboarding.headerUnderstood();
    userAction('use-sort');
  } else if (currentModal === ModalSelect) {
    userAction('use-hide-columns');
  } else if (currentModal === ModalAddColumn) {
    userAction('use-custom-expression');
  } else if (currentModal === ModalCsvUpload) {
    userAction('use-upload-csv');
  } else if (currentModal === ModalRearrangeColumns) {
    userAction('use-reorder-columns');
  } else if (currentModal === ModalRenameColumns) {
    userAction('use-rename-columns');
  }

  return (dispatch, getState) => {
    const isNewWorld = getState().datasource.newWorld;
    return dispatch(
      completed(MODAL_SAVE_CLICKED, {
        modal: currentModal,
        state,
        existingLayer,
        templateToReplace,
        isNewWorld,
      }),
    );
  };
}

// TODO: post react router cleanup.
export function updateExistingQuery(
  existing,
  activeLayers,
  inactiveLayers,
  chartConfig,
  metricsDimensionConfig,
) {
  return saveView({
    existing, // ViewRef
    name: existing.name(),
    activeLayers,
    inactiveLayers,
    chartConfig,
    tags: existing.categories(),
    metricsDimensionConfig,
  });
}

// TODO: post react router cleanup.
function saveView(state) {
  return (dispatch, getState) => {
    dispatch(
      modalInteraction(ModalSaveLayers.modalType(), { section: 'saving' }),
    );
    dispatch(loading(VIEW_SAVED));
    post('saving query', '/views', {
      _existing: state.existing,
      _name: state.name.trim(),
      _categories: state.tags,
      _chartConfig: state.chartConfig,
      _activeLayers: state.activeLayers,
      _inactiveLayers: state.inactiveLayers,
      _parentSeriesId: state.activeLayers[0]?._parent?.series ?? null,
      metricsDimensionConfig: state.metricsDimensionConfig,
    })
      .then(function (json) {
        const email = getState().datasource.userEmail;
        dispatch(completed(MODAL_SAVE_CLICKED, {})); // closes modal
        const savedView = ViewRef.build(json._saved);
        dispatch(
          completed(VIEW_SAVED, {
            userEmail: email,
            json: json._views,
            savedView,
            renaming: state.renaming,
          }),
        );
        dispatch(checkViewStatus(savedView));
        dispatch(loadViewsData()); // reload views data (needed, in particular, to update datasource.totalSavedViews)
      })
      .catch((error) => {
        let finalError = error;
        if (error._reason === 'START_FROM_SAVED_QUERY_CYCLIC_DEPENDENCY') {
          finalError = new BadRequestException({
            json: () => Promise.resolve(error),
          });
        }
        standardErrorHandler(VIEW_SAVED, dispatch)(finalError);
      });
  };
}

function updateCollaborators(state) {
  return (dispatch) => {
    dispatch(
      modalInteraction(ModalCollaborators.modalType(), { section: 'saving' }),
    );
    const emails = state.emails.filter(
      (email) => state.disabled.indexOf(email) === -1,
    );
    const adminEmails = state.admins.filter(
      (email) => emails.indexOf(email) !== -1,
    );
    postJson('/collaborators', { _emails: emails, _adminEmails: adminEmails })
      .then(function () {
        dispatch(completed(MODAL_SAVE_CLICKED, {})); // closes modal
      })
      .catch(standardErrorHandler(MODAL_INTERACTION, dispatch));
  };
}

function updateHiddenColumns(state) {
  return (dispatch) => {
    dispatch(
      modalInteraction(ModalHiddenColumns.modalType(), { section: 'saving' }),
    );
    const hiddenColumns = []; // HiddenColumn objs
    Object.keys(state.nodes).forEach((schema) => {
      const schemaNode = state.nodes[schema];
      if (schemaNode.hidden) {
        hiddenColumns.push(new HiddenColumn(schema));
      }
      Object.keys(schemaNode.nodes).forEach((table) => {
        const tableNode = schemaNode.nodes[table];
        if (tableNode.hidden) {
          hiddenColumns.push(new HiddenColumn(schema, table));
        }
        Object.keys(tableNode.nodes).forEach((column) => {
          const columnNode = tableNode.nodes[column];
          if (columnNode.hidden) {
            hiddenColumns.push(new HiddenColumn(schema, table, column));
          }
        });
      });
    });
    postJson('/hiddenColumns', {
      _hidden: hiddenColumns,
      _hideSqlEditor: state.hideSQLEditor,
    })
      .then(function () {
        dispatch(completed(MODAL_SAVE_CLICKED, {})); // closes modal
        dispatch(loadTables(false));
      })
      .catch(standardErrorHandler(MODAL_INTERACTION, dispatch));
  };
}

export function modalHidden(forceHide = false) {
  if (forceHide) {
    return completed(MODAL_SAVE_CLICKED, {}); // closes modal
  }
  return completed(MODAL_HIDDEN, {});
}

export function loadColumnsForTable(tableOrViewRef) {
  return (dispatch) => {
    const path =
      tableOrViewRef instanceof ViewRef
        ? `/views/${tableOrViewRef.series()}/columns`
        : `/tables/${tableOrViewRef.schema()}.${tableOrViewRef.name()}/columns`;
    dispatch(loading(TABLE_COLUMNS_LOADED));
    fetchJson(path)
      .then(function (json) {
        dispatch(
          completed(TABLE_COLUMNS_LOADED, {
            json,
            table: tableOrViewRef,
          }),
        );
      })
      .catch(standardErrorHandler(TABLE_COLUMNS_LOADED, dispatch));
  };
}

export function openEditDashboardModal(dashboard) {
  return (dispatch) => {
    dispatch(loading(MODAL_DATA_LOADED, { dashboard }));
    fetchJson(`/dashboards/${dashboard.id()}`)
      .then(function (json) {
        dispatch(
          completed(MODAL_DATA_LOADED, {
            json,
            dashboard,
            modal: ModalAddDashboard,
          }),
        );
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}

export function loadCollaborators() {
  return (dispatch) => {
    dispatch(loading(MODAL_DATA_LOADED));
    fetchJson('/collaborators')
      .then(function (json) {
        dispatch(
          completed(MODAL_DATA_LOADED, {
            json,
            modal: ModalCollaborators,
          }),
        );
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}

export function loadHideTables() {
  return (dispatch) => {
    dispatch(loading(MODAL_DATA_LOADED));
    fetchJson('/hiddenColumns')
      .then(function (json) {
        dispatch(
          completed(MODAL_DATA_LOADED, {
            json,
            modal: ModalHiddenColumns,
          }),
        );
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}
